import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { FaPlus, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import u2000FormState from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import type { U3121Employee } from "../../../../U3121Service";
import { NS, U3121ROLES } from "../../../../U3121";
import type { U3121ProjectFormExtendedHandlers } from "../u3121ProjectContactsTab/U3121ProjectContactHandlers";
import { u3121ProjectTimesheetAccessHandler } from "./U3121ProjectTimesheetAccessHandlers";
import U3121ProjectTimesheetAccessEditDlg from "./U3121ProjectTimesheetAccessEditDlg";
import U3121ProjectTimesheetAccessDeleteDlg from "./U3121ProjectTimesheetAccessDeleteDlg";

/** Aplatit un item renvoyé par l'API (forme { employee: {...} } ou déjà plat { id, firstName, ... }). */
function normalizeTimesheetAccessItem(item: U3121Employee | { employee?: U3121Employee; id?: string }): U3121Employee {
  if (!item) return { id: "", firstName: "", lastName: "", codeEmployee: "" };
  const emp = (item as { employee?: U3121Employee }).employee;
  if (emp && (emp.id || emp.firstName || emp.lastName || emp.codeEmployee)) {
    return { id: emp.id ?? "", firstName: emp.firstName, lastName: emp.lastName, codeEmployee: emp.codeEmployee };
  }
  return item as U3121Employee;
}

function displayEmployeeLabel(row: U3121Employee): string {
  if (!row) return "";
  const firstName = row.firstName ?? "";
  const lastName = row.lastName ?? "";
  const code = row.codeEmployee ?? "";
  const name = [firstName, lastName].filter(Boolean).join(" ").trim();
  return code ? `${name} (${code})` : name || row.id || "";
}

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    {
      ...U2000IdColumnDef,
    },
    {
      name: "codeEmployee",
      label: t("lblEmployee", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-full",
      type: "string",
      align: "left",
      display: (_column: U2000TableColumn, _value: unknown, row: U3121Employee) => displayEmployeeLabel(row),
    },
  ];
}

interface U3121ProjectTimesheetAccessBodyProps {
  formStateHandlers: U3121ProjectFormExtendedHandlers;
}

/**
 * Onglet Accès feuille de temps projet : liste des employés ayant accès à la feuille de temps.
 */
const U3121ProjectTimesheetAccessBody: React.FC<U3121ProjectTimesheetAccessBodyProps> = ({ formStateHandlers }) => {
  const { t, i18n } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const project = formStateHandlers.getData?.() ?? null;
  const timesheetAccess = project?.timesheetAccess ?? [];

  const timesheetAccessDataKey = useMemo(() => {
    const arr = Array.isArray(timesheetAccess) ? timesheetAccess : [];
    const ids = arr.map((r) => normalizeTimesheetAccessItem(r as U3121Employee | { employee?: U3121Employee }).id).filter(Boolean).sort();
    return `${project?.id ?? ""}_${arr.length}_${ids.join(",")}`;
  }, [project?.id, timesheetAccess]);

  const [formState, formStateDispatch] = React.useReducer(u2000FormState, u2000IntFormState);
  const gridStateHandlers = useU2000GridStateHandlers<U3121Employee>();

  const businessHandlers: U2000FormSBusinessHandlers<U3121Employee> = useMemo(
    () => ({
      newRecord: () => ({ id: "", firstName: "", lastName: "", codeEmployee: "" }),
      validateForm: (data) => {
        const result: Record<string, string> = {};
        if (!data?.id?.trim()) {
          result["id"] = t("errTimesheetAccessEmployeeRequired", { ns: NS });
        }
        return result;
      },
    }),
    [t],
  );

  const assignmentFormStateHandlers = useU2000FormStateHandlers<U3121Employee>({
    formState,
    formStateDispatch,
    businessHandlers,
  });

  const handler = u3121ProjectTimesheetAccessHandler({
    gridStateHandlers,
    t,
    formStateHandlers: assignmentFormStateHandlers,
    parentFormHandlers: formStateHandlers,
  });

  useEffect(() => {
    const columns = getColumnsDef(t);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" as const })));
    const rawList = Array.isArray(timesheetAccess) ? timesheetAccess : [];
    const list = rawList.map((r) => normalizeTimesheetAccessItem(r as U3121Employee | { employee?: U3121Employee }));
    gridStateHandlers.setData(list);
    gridStateHandlers.setTotalRows(list.length);
    gridStateHandlers.setErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridStateHandlers volontairement exclu des deps
  }, [timesheetAccessDataKey, t, i18n.language]);

  useEffect(() => {
    if (handler.dataChangedEvent === 0) return;
    const proj = formStateHandlers.getData();
    if (!proj) return;
    formStateHandlers.setData({
      ...proj,
      timesheetAccess: gridStateHandlers.getData(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formStateHandlers/gridStateHandlers exclus pour éviter récursion
  }, [handler.dataChangedEvent]);

  const isEditMode = formStateHandlers.getFormState?.() === U2000FormStateEnumState.EDIT;
  const disabledDelete = !isEditMode || handler.gridStateHandlers.getSelectedRows().length === 0;

  const actions = useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: handler.onAdd,
        hasRole: hasRole(U3121ROLES.EDIT),
        disabled: !isEditMode,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: handler.onDelete,
        hasRole: hasRole(U3121ROLES.EDIT),
        disabled: disabledDelete,
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, handler, isEditMode, disabledDelete, hasRole],
  );

  return (
    <>
      <U3121ProjectTimesheetAccessEditDlg handler={handler} t={t} NS={NS} />
      <U3121ProjectTimesheetAccessDeleteDlg handler={handler} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        rowClickFn={(row) => handler.onRowClick(row as U3121Employee)}
        allowRowSelection={isEditMode}
        label={t("lblTimesheetAccess", { count: gridStateHandlers.getTotalRows(), ns: NS })}
      />
    </>
  );
};

export default U3121ProjectTimesheetAccessBody;
